import UIUtils from 'js/lib/UIUtils';
import { getExistingReusableCustomProcessorId } from 'js/lib/sourceCustomProcessor/sourceCustomProcessorUtils';
import _, {
  assign,
  difference,
  filter,
  forEach,
  groupBy,
  has,
  head,
  isEmpty,
  keys,
  map,
  omit,
  reduce,
  split,
  values,
} from 'lodash';
import Constants, { CONNECTORS_WITHOUT_PATH } from 'js/Constants';
import { Base64 } from 'js-base64';
import createFileSystemService from 'new/components/flows/configure/FileSystem/service/createFileSystemService';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_NOSQL,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
} from 'js/constants/connectionCategories';
import createDatabaseService from 'new/components/flows/configure/Database/service/createDatabaseService';
import { formatNUIURL, isNUIURLEquals, isNUIURLStartsWith } from 'js/lib/URLUtils';
import { ROUTE_PIPELINES, ROUTE_SELECT_PIPELINE_TYPE } from 'js/constants/pathnames';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { FileUtil } from 'new/utils/FileUtil';
import flowTypes from 'new/components/flows/types/flowTypes';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
const { buildSelectedBucket } = FileUtil;

export default function createFlowService() {
  const fileService = createFileSystemService();
  const databaseService = createDatabaseService();

  function getConnectionType(selectedConnection, selectedCredentialInfo) {
    return selectedCredentialInfo?._vendorConnectionType || selectedConnection;
  }

  function createDefaultSourceConfig({
    sourceConfig = {},
    configFields,
    selectedConnection,
    orgId,
  }): { [key: string]: any } {
    //check if any conditional params are to be rendered. if they are missing in source config add their default value.
    sourceConfig = UIUtils.configWithMissingDefaults(sourceConfig, configFields);

    if (
      (selectedConnection === 'gdrive' && sourceConfig['ui.selected_file_mime_type']) ||
      !sourceConfig['ignore.files.older.than.ms']
    ) {
      delete sourceConfig['ignore.files.older.than.ms'];
    }

    const hcFields = filter(configFields, (field) =>
      Constants.HC_SOURCE_FIELD_NAMES.includes(field.name),
    );

    if (UIUtils.isHCEnabled(orgId, selectedConnection)) {
      sourceConfig = UIUtils.configWithMissingDefaults(sourceConfig, hcFields);

      forEach(hcFields, (field) => {
        if (!UIUtils.shouldRenderField(field, sourceConfig)) {
          delete sourceConfig[field.name];
        }
      });
    } else {
      forEach(hcFields, (field) => {
        delete sourceConfig[field.name];
      });
    }

    sourceConfig = UIUtils.reduceConfig(sourceConfig, configFields);

    return sourceConfig;
  }

  function createDefaultSinkConfig({
    selectedConnection,
    configFields,
    dataSchema,
    connectionCategory,
    allowSinkAutoMapping,
    buildNew = false,
    isEditMode = false,
    orgId,
    sinkConfig,
  }) {
    let currentSinkConfig;

    if (buildNew) {
      const sinkMapping = UIUtils.getSinkMappingFromAttributes(
        getSinkMappingAttributes({
          sinkConfig: null,
          connectionCategory,
          allAttributes: dataSchema,
        }),
      );
      const sinkConfigWithMapping = getSinkConfigWithMapping({
        sinkConfig: null,
        sinkMapping,
        connectionCategory,
        isEditMode,
      });

      if (connectionCategory === CONNECTION_CATEGORY_DATABASE && allowSinkAutoMapping) {
        sinkConfigWithMapping.mapping = { mode: 'auto' };
      }

      currentSinkConfig = sinkConfigWithMapping;
    } else {
      currentSinkConfig = { ...sinkConfig };
    }

    currentSinkConfig = UIUtils.configWithMissingDefaults(currentSinkConfig, configFields);
    currentSinkConfig.sink_type = selectedConnection;

    setHCInSinkConfig(currentSinkConfig, {
      sinkConfigFields: configFields,
      orgId,
      selectedConnection,
    });

    return currentSinkConfig;
  }

  function buildSinkConfig({
    newSinkConfig,
    sinkConfigFields,
    field,
    value,
    selectedConnection,
    orgId,
    flowType,
  }) {
    let sinkConfig = UIUtils.fillConditionalDefault(
      { ...newSinkConfig, 'ui.flow_type': flowType },
      sinkConfigFields,
      field,
      value,
    );

    setHCInSinkConfig(sinkConfig, {
      selectedConnection,
      orgId,
      sinkConfigFields,
    });

    return sinkConfig;
  }

  function setHCInSinkConfig(sinkConfig, { orgId, sinkConfigFields, selectedConnection }) {
    const hcFields = filter(sinkConfigFields, (field) =>
      Constants.HC_SINK_FIELD_NAMES.includes(field.name),
    );

    if (UIUtils.isHCEnabled(orgId, selectedConnection, false)) {
      sinkConfig = UIUtils.configWithMissingDefaults(sinkConfig, hcFields);
      forEach(hcFields, (field) => {
        if (!UIUtils.shouldRenderField(field, sinkConfig)) {
          delete sinkConfig[field.name];
        }
      });
    } else {
      forEach(hcFields, (field) => {
        delete sinkConfig[field.name];
      });
    }
  }

  type Getter = (connection: string) => string | undefined;

  type Builder = (
    connection: string,
    sourceConfig: any,
    sourceConfigFields: any[],
  ) => string | undefined;

  function pipeInitialSourceConfigBuilder(
    connectionCategory: string,
    sourceConfig: any,
    sourceConfigFields: any[],
  ): (...builders: Builder[]) => string | undefined {
    return (...builders: Builder[]) => {
      return builders.reduce((value, builder) => {
        if (value) return value;
        return builder(connectionCategory, sourceConfig, sourceConfigFields);
      }, '');
    };
  }

  function pipeNameGetters(connectionCategory): (...getters: Getter[]) => string | undefined {
    return (...getters: Getter[]) => {
      return getters.reduce((value, getter) => {
        if (value) return value;
        return getter(connectionCategory);
      }, '');
    };
  }

  function getSourceName({
    selectedBucket = { displayName: '', displayPath: '' },
    selectedTable = {},
    selectedCredential,
    connectionCategory,
  }) {
    function defaultSourceName() {
      return (connectionCategory: string) => {
        const { displayPath, displayName } = selectedBucket;
        let leafNodeName;
        if (displayName) {
          leafNodeName = displayName;
        } else if (displayPath) {
          leafNodeName = displayPath.substr(displayPath.lastIndexOf('/') + 1);
        }
        //TODO: ftp etc source types have bucket name /. Need backend to change the types maybe?
        return leafNodeName;
      };
    }

    return pipeNameGetters(connectionCategory)(
      databaseService.getResourceName(selectedTable, selectedCredential),
      defaultSourceName(),
    );
  }

  function getSinkName({
    selectedBucket = {},
    selectedTable = {},
    connectionCategory,
    dataMapSettings = { name: '' } as any,
    selectedConnection,
    selectedCredential,
    metaInfo = { displayName: '' },
  }) {
    function defaultSinkName() {
      return (connectionCategory: string) => {
        const name =
          (dataMapSettings && dataMapSettings.name) ||
          (selectedCredential && selectedCredential.name);
        return !name || !selectedConnection ? null : `${metaInfo.displayName} - ${name}`;
      };
    }

    return pipeNameGetters(connectionCategory)(
      fileService.getSinkName(selectedBucket),
      databaseService.getResourceName(selectedTable, selectedCredential),
      defaultSinkName(),
    );
  }

  function getPathToGoAfterCreation(
    previousPath = '',
    queryParams?: Record<string, string>,
    options?: {
      resourceType?: 'source' | 'sink' | 'transform' | 'flow';
      nextPath?: string;
      flowType?: string;
      flowOriginId?: string | number;
    },
  ) {
    if (options?.nextPath) {
      return options.nextPath;
    }

    // Navigate to edit screen for new flows created from source
    if (options?.resourceType === 'source' && options?.flowOriginId && options?.flowType) {
      return `${ROUTE_CONNECT}${ROUTE_PIPELINES}/${options.flowType}/${options.flowOriginId}/edit`;
    }

    // Default: use previous path logic
    let path = previousPath;
    if (!previousPath) {
      path = formatNUIURL(ROUTE_CONNECT, `${ROUTE_PIPELINES}`);
    } else if (
      isNUIURLStartsWith(previousPath, `${ROUTE_CONNECT}${ROUTE_PIPELINES}/new`) ||
      isNUIURLStartsWith(previousPath, `${ROUTE_CONNECT}${ROUTE_SELECT_PIPELINE_TYPE}`)
    ) {
      path = formatNUIURL(ROUTE_CONNECT, `${ROUTE_PIPELINES}`);
    }

    if (queryParams && Object.keys(queryParams).length > 0) {
      // Parse existing query parameters from the path
      const url = new URL(path, window.location.origin);
      const searchParams = new URLSearchParams(url.search);

      // Add or replace the new query parameters
      Object.entries(queryParams).forEach(([key, value]) => {
        searchParams.set(key, value);
      });

      // Reconstruct the URL with updated query parameters
      url.search = searchParams.toString();
      path = url.pathname + url.search;
    }

    return path;
  }

  function getSinkMappingAttributes({ connectionCategory, sinkConfig, allAttributes }) {
    if (
      allAttributes &&
      allAttributes.length &&
      (connectionCategory === CONNECTION_CATEGORY_DATABASE ||
        (connectionCategory === CONNECTION_CATEGORY_FILE &&
          UIUtils.formatsWithColumns.indexOf(sinkConfig && sinkConfig.data_format) > -1))
    ) {
      // exclude attributes controlled by arrays
      const attributesByName = groupBy(allAttributes, '__name');
      return filter(
        allAttributes,
        ({ __controlledBy }) =>
          !attributesByName[__controlledBy] ||
          attributesByName[__controlledBy][0].__type !== 'array',
      );
    }

    return allAttributes;
  }

  function buildBucketObject(table, database) {
    let bucketObject = {
      table: null,
      database: database,
    };

    //TODO: Need to fix with Database name
    if (table) {
      bucketObject = assign({}, bucketObject, omit(table, ['scanned', 'columns', 'name', 'id']));
      bucketObject.table = table?.name || null;
    }
    return bucketObject;
  }

  function getSinkConfigWithMapping({ sinkConfig, sinkMapping, connectionCategory, isEditMode }) {
    const newSinkConfig = { ...sinkConfig };
    if (connectionCategory === CONNECTION_CATEGORY_NOSQL) {
      return newSinkConfig;
    }

    const mappingForBuild = UIUtils.getMappingForBuild(connectionCategory, sinkMapping);

    newSinkConfig.mapping = UIUtils.buildMapping(
      connectionCategory,
      newSinkConfig.data_format,
      mappingForBuild,
      sinkConfig?.mapping,
      isEditMode ? undefined : { mode: 'manual' },
    );

    return newSinkConfig;
  }

  type CreateInitialSourceConfigOptions = {
    empty: boolean;
    sourceConfig: any;
    sourceConfigFields: any;
    flowType: keyof typeof flowTypes;
    connectionCategory: string;
  };

  function createInitialSourceConfig({
    empty = false,
    sourceConfig,
    sourceConfigFields,
    flowType = null,
    connectionCategory,
  }: CreateInitialSourceConfigOptions) {
    let currentSourceConfig = !sourceConfig || empty ? {} : { ...this.props.sourceConfig };
    currentSourceConfig['ui.flow_type'] = flowType;

    // TEMP HACK:
    // The two calls to configWithMissingDefaults() are a hacky solution for the
    // case where the first call sets some default values on which other fields'
    // defaults depend.
    // The correct solution would be to update configWithMissingDefaults such
    // that it recursively sets all defaults, then the defaults depending on the
    // new config, and so on until no unaddressed conditional defaults remain.
    currentSourceConfig = UIUtils.configWithMissingDefaults(
      UIUtils.configWithMissingDefaults(currentSourceConfig, sourceConfigFields, true),
      sourceConfigFields,
      true,
    );

    currentSourceConfig = pipeInitialSourceConfigBuilder(
      connectionCategory,
      currentSourceConfig,
      sourceConfigFields,
    )(fileService.getInitialSourceConfig());

    // omit fields which shouldn't be rendered
    const visibleFieldData = UIUtils.omitInvisibleFieldData(
      sourceConfigFields,
      currentSourceConfig,
    );

    if (isEmpty(visibleFieldData)) {
      return currentSourceConfig;
    } else {
      return visibleFieldData;
    }
  }

  function getInitialSinkConfigToDatabaseConnector(info: any) {
    const { sink, dataSchema, sinkConfig, connectionCategory } = info;

    if (dataSchema) {
      const sinkMapping = UIUtils.getSinkMappingFromAttributes(
        getSinkMappingAttributes({
          connectionCategory,
          sinkConfig,
          allAttributes: dataSchema,
        }),
      );

      const sinkConfigWithMapping = getSinkConfigWithMapping({
        sinkConfig,
        sinkMapping,
        connectionCategory,
        isEditMode: !!sink?.id,
      });

      return { sinkMapping, sinkConfig: sinkConfigWithMapping };
    }

    return { sinkConfig };
  }

  const initializeFormToFileCategory = (sourceConfig: any, source = null, transforms = null) => {
    if (!sourceConfig) return {};
    let effectivePath = undefined;
    const { bucket, prefix, path, 'ui.display_path': displayPath } = sourceConfig;

    if (path) {
      // Normalize: legacy BYO code accidentally stored a Bucket object instead of a string.
      effectivePath = typeof path === 'string' ? path : (path as any)?.path;
    } else if (bucket) {
      effectivePath = bucket;

      if (prefix) {
        const separator = effectivePath.endsWith('/') || prefix.startsWith('/') ? '' : '/';
        effectivePath += `${separator}${prefix}`;
      }
    }

    const selectedBucket = buildSelectedBucket(effectivePath, displayPath);
    let newSourceConfig = _.omit(sourceConfig, ['bucket', 'prefix']);
    sourceConfig.path = effectivePath;

    if (displayPath) newSourceConfig['ui.display_path'] = displayPath;

    // set customProcessorId/customProcessorCode if applicable
    const transformId = source?.code_container_id;
    const reusableCustomProcessorId = getExistingReusableCustomProcessorId(source);
    const customProcessorMode = source?.source_config?.['ui.custom_processor_mode'];

    if (customProcessorMode === 'custom_code' && transforms?.[transformId]) {
      newSourceConfig = {
        ...newSourceConfig,
        customProcessorCode: Base64.decode(transforms[transformId].code),
      };
    } else if (customProcessorMode === 'reusable_code_container' && reusableCustomProcessorId) {
      newSourceConfig = {
        ...newSourceConfig,
        customProcessorId: reusableCustomProcessorId,
      };
    }

    return { config: newSourceConfig, selectedBucket };
  };

  const defaultSinkValidate = (
    sinkConfig: any,
    sinkConfigFields: any,
    onError: (message: string) => void,
  ) => {
    if (sinkConfig && sinkConfig['insert.mode'] === 'UPSERT' && !sinkConfig['primary.key']) {
      onError('Please configure a primary key for this destination');
      return false;
    }

    let mappedFields = [];
    if (sinkConfig.mapping && sinkConfig.mapping.mapping) {
      mappedFields = map(values(sinkConfig.mapping.mapping), (value) => head(keys(value)));
    }
    let primaryKeys = split(sinkConfig['primary.key'], ',');
    let notMappedKeys = difference(primaryKeys, mappedFields);
    if (
      sinkConfig &&
      sinkConfig['insert.mode'] === 'UPSERT' &&
      sinkConfig['primary.key'] &&
      notMappedKeys.length > 1
    ) {
      onError('Primary key should be mapped to an attribute for this destination.');
      return false;
    }

    const missingFields = getEmptyRequiredFields(sinkConfigFields, sinkConfig);
    if (!isEmpty(missingFields)) {
      const errorMessage = reduce(
        missingFields,
        (message, field) => `${message} ${field.display_name},`,
        'Please enter the following required fields: ',
      );
      onError(errorMessage);
      return false;
    }

    return true;
  };

  const defaultSourceValidate = (
    {
      sourceConfig,
      sourceConfigFields,
      selectedConnection,
      selectedBucket,
      connectionCategory,
    }: {
      sourceConfig: any;
      sourceConfigFields: any;
      selectedConnection: string;
      selectedBucket: any;
      connectionCategory: string;
    },
    onError: (message: string) => void,
  ) => {
    if (
      connectionCategory !== CONNECTION_CATEGORY_REST &&
      connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_API
    ) {
      // When it's rest or templatized api, the fields validation is handled by the SourceConfigureStep component
      const missingFields = getEmptyRequiredFields(sourceConfigFields, sourceConfig);
      if (!isEmpty(missingFields)) {
        const errorMessage = reduce(
          missingFields,
          (message, field) => `${message} ${field.display_name},`,
          'Please enter the following required fields: ',
        );
        onError(errorMessage);
        return false;
      }
    }

    if (!CONNECTORS_WITHOUT_PATH.includes(selectedConnection) && !selectedBucket) {
      switch (connectionCategory) {
        case CONNECTION_CATEGORY_FILE:
          onError('Please select a folder to scan.');
          return;

        case CONNECTION_CATEGORY_NOSQL:
          onError('Please select a collection to scan.');
          return;

        case CONNECTION_CATEGORY_DATABASE:
          if (!sourceConfig.db_query_mode || sourceConfig.db_query_mode === 'Default') {
            onError('Please select a table to scan.');
            return;
          }
          break;
      }
    }
    return true;
  };

  return {
    getConnectionType,
    createInitialSourceConfig,
    createDefaultSourceConfig,
    createDefaultSinkConfig,
    getSinkName,
    getSourceName,
    buildSinkConfig,
    buildBucketObject,
    getSinkMappingAttributes,
    getSinkConfigWithMapping,
    setHCInSinkConfig,
    getInitialSinkConfigToDatabaseConnector,
    initializeFormToFileCategory,
    getPathToGoAfterCreation,
    defaultSinkValidate,
    defaultSourceValidate,
  };
}
